import React, { useContext, useRef, useState } from 'react';
import {
  AllJobCarrierFilter,
  CarrierExpeditionId,
  CarrierExpeditionStatusEnum,
  CargoPreviewPlanOutDTO,
  EntityTypeEnum,
  JobPreviewOutDTO,
  LocationJobPreviewOutDTO,
  OrderPreviewExpeditionListOutDTO,
  Pageable,
  PermissionTypesEnum,
} from '@api/ringil-api.v2';
import { isNotBlank, useLocalStorage } from '@utils/utils';
import { ApiContext } from '@api/api';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { Toast } from 'primereact/toast';
import styled from 'styled-components';
import useNavigation from '@hooks/useNavigation';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import useCarrierDispatcher from '@hooks/use-carrier-dispatcher/use-carrier-dispatcher.tsx';
import UploadAttachmentDialog from '@components/ringil3/Features/Attachments/UploadAttachmentDialog.tsx';
import {
  useCreateMultiEditCarInfoSidebar,
} from '@app/pages/carrier/shipments-list/sidebars/MultiEditCarInfoSidebar.tsx';
import BreadCrumbComponent from '@components/obsolete/BreadCrumbComponent/BreadCrumbComponent.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import RingilDataTable, {
  ColumnConfig,
  DateTimeFormatType,
  FilterConfig,
  FilterType,
  MassActionConfig,
  MobileCard,
  MobileCardContent,
  MobileCardHeader,
  MobileFileTag,
  MobileInfoTag,
  MobileLabelDiv,
  MobileSection,
  MobileSectionTitle,
  PagedData,
} from '@components/RingilDataTable/RingilDataTable.tsx';
import SvgFile from '@icons/File.tsx';
import SvgMoney from '@components/ringil3/icons/Money.tsx';
import SvgTruck from '@icons/TruckSvg.tsx';
import SvgBuilding from '@icons/BuildingSvg.tsx';
import SvgRoute from '@components/ringil3/icons/heading/Route.tsx';
import LinkSvg from '@icons/LinkSvg.tsx';
import { useMediaQuery } from 'react-responsive';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import ExpeditionStateSpan from '@components/obsolete/StateSpan/ExpeditionStateSpan.tsx';
import useCountries from '@hooks/use-countries/useCountries.tsx';
import SvgRepeatBack from '@icons/arrows-repeat.svg?react';
import SvgCreated from '@icons/info.svg?react';
import SvgCalendar from '@icons/calendar.svg?react';
import dayjs from 'dayjs';
import {
  formatOnlyDayMonthFromDayJs,
  formatOnlyDayMonthFromDayjsInTz,
  formatDateFromDayjsInTzWithLocale,
  formatDateTimeFromDayjsInTzWithLocale,
  formatTimeIntervalFromDayjsIntervalWithLocale,
  dayjsFromApiUtcInTz,
  getBrowserTimezone,
} from '@utils/dateWithTz.tsx';
import { formatTimeslotInterval } from '@utils/timeslotDateTime.tsx';
import SvgAlert from '@icons/Alert.tsx';
import ConditionalLinkedResBadge from '@app/pages/carrier/shipments-list/ConditionalLinkedResBadge.tsx';
import ExpandableLabelWrapper from '@components/RingilDataTable/ExpandableLabelWrapper.tsx';
import useCustomersForCarrier from '@hooks/use-customers-for-carrier/use-customers-for-carrier.tsx';
import { formatPrice } from '@utils/priceUtils.ts';
import ExpandableInfoAllTag from '@components/RingilDataTable/ExpandableInfoAllTag.tsx';
import clsx from 'clsx';
import SvgChevron from '@icons/chevron.svg?react';
import ExpandableInfoTag from '@components/RingilDataTable/ExpandableInfoTag.tsx';
import SafeHtml from '@components/RingilComponents/RichTextEditor/SafeHtml.tsx';
import { noteToPlainText } from '@components/RingilComponents/RichTextEditor/sanitizeNoteHtml';
import CustomTooltip from '@components/ringil3/Tooltip/CustomTooltip.tsx';
import SvgLink from '@icons/link.svg?react';
import SvgOrder from '@icons/order.svg?react';
import AttachmentLink from '@components/RingilDataTable/AttachmentLink.tsx';
import { truncateFileName } from '@utils/fileUtils.ts';
import { useInputDrivers } from '@components/obsolete/Form/InputDriver/InputDrivers.tsx';
import COTimezone from '@components/FigmaComponents/COTimezone.tsx';
import CountryFlag from '@components/framework/CountryFlag/CountryFlag.tsx';
import { useSecuredFlag } from '@components/security/useSecuredFlag';

enum InfoTagEnum {
  ALL = 'all',
  START_DATE = 'startDate',
  EXPEDITION_TYPE = 'expeditionType',
  STOPS = 'stops',
  ORDERS = 'orders',
  EXTRA_COSTS = 'extraCosts',
  NOTE = 'note',
  ATTACHMENTS = 'attachments',
  LAST_MODIFIED_DATE = 'lastModifiedDate',
  CREATED_DATE = 'createdDate',
}

const ResponsiveShipmentList = () => {
  const { t } = useTranslation('common');
  const { tr } = useTranslationLgs();
  const { caListShipments, downloadAttachment } = useContext(ApiContext);

  const [visibleFileUploadDialog, setVisibleFileUploadDialog] = useState(false);
  const toast = useRef(undefined);
  const navigation = useNavigation();
  const { hasPermission, loggedUser } = useContext(AuthenticatedUserContext);
  const attachmentNameFilterFlag = useSecuredFlag('carrier-shipments-attachment-name-filter', {
    kind: 'visibility',
    description: 'Filter jobs by attachment name',
    permissions: [PermissionTypesEnum.PERMISSION_EXPEDITION_DETAIL_ATTACHMENTS_READ, PermissionTypesEnum.PERMISSION_EXPEDITION_DETAIL_ATTACHMENTS_RW],
  });
  const [expandedLabels, setExpandedLabels] = useLocalStorage('EXPEDITION_LIST_EXPANDED_LABELS', false);


  const initialExpandedInfoTags: Record<InfoTagEnum, boolean> = Object.values(InfoTagEnum).reduce(
    (acc, key) => ({ ...acc, [key]: false }),
    {} as Record<InfoTagEnum, boolean>,
  );

  const [expandedInfoTags, setExpandedInfoTags] = useLocalStorage<Record<InfoTagEnum, boolean>>(
    'CARRIER_SCHIPMENT_LIST_EXPANDED_INFO_TAGS',
    initialExpandedInfoTags,
  );

  const { drivers, loading: lazyLoading, onLazyLoad } = useInputDrivers();
  const dispatchers = useCarrierDispatcher();
  const countries = useCountries();
  const customers = useCustomersForCarrier(''); // no query filter, get all

  const isMobile = useMediaQuery({ maxWidth: 1024 });

  const enc = (applicationId: string) => btoa(applicationId).replace(/[/=]/g, '');

  const toggleInfoExpansion = (key: InfoTagEnum) => {
    setExpandedInfoTags(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const toggleLabelsExpansion = () => {
    setExpandedLabels(prev => !prev);
  };

  const handleFileClick = (attachmentId: string, fileName: string) => {
    if (downloadAttachment) {
      downloadAttachment(attachmentId, (data: { mimeType: string; data: string }) => {
        const linkSource = `data:${data.mimeType};base64,${data.data}`;
        const downloadLink = document.createElement('a');
        downloadLink.href = linkSource;
        downloadLink.download = fileName;
        downloadLink.click();
      });
    } else {
      console.warn('downloadAttachment function is not available in ApiContext');
    }
  };

  const loadDataApi = (filter: AllJobCarrierFilter, pageable: Pageable, onSuccess: (pagedData: PagedData<JobPreviewOutDTO>) => void) => {
    const normalizedFilter = {
      ...filter,
      userTimezoneId: getBrowserTimezone(),
    };
    caListShipments(
      normalizedFilter,
      pageable,
      data => {
        onSuccess({
          data: data.data,
          total: data.total,
          page: data.page,
          size: data.size
        });
      }
    )
  };

  const TenderAlert = ({ message }: { message: string }) => (
    <span className="flex items-center gap-2 text-[#343333] font-dm-sans text-[14px] font-normal leading-normal">
      <SvgAlert fill={'var(--ringil3-complementary-warning)'} width={"16px"} height={"16px"} />
      {message}
    </span>
  );

  const renderExpeditionStatus = (rowData: JobPreviewOutDTO) => {
    const applicationId = rowData.applicationId;
    const missingTimeslots = !rowData.jobAlerts.hasSelectedTimeslots;
    const missingCarPlate = !rowData.carPlate;
    const missingReservation = !rowData.jobAlerts.hasRequiredReservations;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        <span
          data-testid={`carrier-expeditions-row-${applicationId}-state`}
          data-expedition-status={rowData.expeditionStatus ?? ''}
          style={{ display: 'none' }}
        />
        <ExpeditionStateSpan expeditionStage={rowData.expeditionStatus} isManual={rowData.isManualExpedition} />
        <div>
          {missingTimeslots && (
            <TenderAlert message={tr('CarrierShipmentsList.missingTimeslotsAlert', 'Missing timeslots')} />
          )}
          {missingCarPlate && (
            <TenderAlert message={tr('CarrierShipmentsList.carPlateAlert', 'No car plate assigned')} />
          )}
          {missingReservation && (
            <TenderAlert message={tr('CarrierShipmentsList.reservationAlert', 'Missing reservation')} />
          )}
        </div>
      </div>
    );
  };

  const renderLocationDateTime = (location: LocationJobPreviewOutDTO): React.ReactNode => {
    if (location.completedAtUtc) {
      const completedDayjs = dayjsFromApiUtcInTz(location.completedAtUtc, location.timezoneId);
      return formatDateTimeFromDayjsInTzWithLocale(completedDayjs, location.timezoneId, ' | ');
    }
    const slot = location.timeslots?.[0];
    if (slot) {
      const utcStart = slot.utcStart ?? slot.sinceInUTC;
      const utcEnd = slot.utcEnd ?? slot.tillInUTC;
      const tz = slot.timezoneId ?? location.timezoneId;
      if (utcStart && tz) {
        const startDayjs = dayjsFromApiUtcInTz(utcStart, tz);
        if (utcEnd) {
          const endDayjs = dayjsFromApiUtcInTz(utcEnd, tz);
          return `${formatDateFromDayjsInTzWithLocale(startDayjs, tz)} | ${formatTimeIntervalFromDayjsIntervalWithLocale(startDayjs, endDayjs, tz, '-')}`;
        }
        return formatDateTimeFromDayjsInTzWithLocale(startDayjs, tz, ' | ');
      }
    }
    return formatTimeslotInterval(location.globalTimeslots);
  };

  const renderRoute = (rowData: JobPreviewOutDTO) => {
    const applicationId = rowData.applicationId;
    const firstLocation = rowData.locations[0];
    const lastLocation = rowData.locations[rowData.locations.length - 1];

    const fromNameId = `route-from-name_${enc(applicationId)}`;
    const toNameId = `route-to-name_${enc(applicationId)}`;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch' }} className="min-w-0 w-full">
        <div data-testid={`carrier-expeditions-row-${applicationId}-route-from`} className="flex items-center gap-1 min-w-0">
          <CountryFlag code={firstLocation.country} />
          <b className="flex-shrink-0">{firstLocation.country}</b>
          <span id={fromNameId} className="truncate">{firstLocation.locationName}</span>
          <CustomTooltip targetSelector={`#${fromNameId}`} content={firstLocation.locationName} />
        </div>
        <SmallerAndGrayDiv data-testid={`carrier-expeditions-row-${applicationId}-route-from-dateRange`}>{renderLocationDateTime(firstLocation)}</SmallerAndGrayDiv>
        <COTimezone dataTestId={`carrier-expeditions-row-${applicationId}-route-from-timezone`} timezoneId={firstLocation.timezoneId} />
        <ConditionalLinkedResBadge route={rowData.routes[0]} />
        {rowData.locations.length > 1 && (
          <>
            <div style={{ marginTop: '0.5rem' }} data-testid={`carrier-expeditions-row-${applicationId}-route-to`} className="flex items-center gap-1 min-w-0">
              <CountryFlag code={lastLocation.country} />
              <b className="flex-shrink-0">{lastLocation.country}</b>
              <span id={toNameId} className="truncate">{lastLocation.locationName}</span>
              <CustomTooltip targetSelector={`#${toNameId}`} content={lastLocation.locationName} />
            </div>
            <SmallerAndGrayDiv data-testid={`carrier-expeditions-row-${applicationId}-route-to-dateRange`}>{renderLocationDateTime(lastLocation)}</SmallerAndGrayDiv>
            <COTimezone dataTestId={`carrier-expeditions-row-${applicationId}-route-to-timezone`} timezoneId={lastLocation.timezoneId} />
            <ConditionalLinkedResBadge route={rowData.routes[rowData.routes.length - 1]} />
          </>
        )}
      </div>
    );
  };

  const setAllTagsExpanded = (expanded: boolean) => {
    setExpandedInfoTags(_ => {
      const allInfoTagKeys = Object.values(InfoTagEnum);
      const newState = {} as Record<InfoTagEnum, boolean>;
      allInfoTagKeys.forEach(key => {
        newState[key] = expanded;
      });
      return newState;
    });
  };

  const renderAdditionalInfo = (row: JobPreviewOutDTO) => {
    const handleTagClick = (key: InfoTagEnum) => {
      toggleInfoExpansion(key);
    };

    const handleAllTagClick = (key: InfoTagEnum) => {
      setAllTagsExpanded(!expandedInfoTags[key]);
    };

    const stopCount = row.locations.length - 2;
    const missingStopsTimeslots = !row.jobAlerts.hasSelectedTimeslotsOnStops;
    const orderCount = row.orders?.length || 0;
    const ordersTooltipContent =
      row.orders
        ?.map((order: OrderPreviewExpeditionListOutDTO) => {
          const itemsList = order.items
            ?.filter(item => item.name)
            ?.map((item: CargoPreviewPlanOutDTO) => `${item.name}: ${item.amount}`)
            ?.join(', ');

          return order.orderName ? `${order.orderName}${itemsList ? ' - ' + itemsList : ''}` : '';
        })
        ?.join(' | ') || '';

    const stops = row.locations
      ?.slice(1, -1)
      .map(loc => loc.locationName)
      .join(', ') || '';

    const stopsWithReservationBadges = row.locations
      ?.slice(1, -1)
      .map((loc, index) => {
        const routeIndex = index + 1; // Adjust for slice offset
        const route = row.routes?.[routeIndex];

        return (
          <span key={index} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', marginRight: '0.5rem' }}>
            <span>{loc.locationName}</span>
            <ConditionalLinkedResBadge route={route} />
          </span>
        );
      }) || [];

    const extraCostsTooltipContent = row.extraCosts?.map(ecItem => `${ecItem.name}: ${formatPrice(ecItem.price, ecItem.currency)}`).join('; ') || '';
    const expeditionType = !row.expeditionType ? null : tr(`global.expeditionTypeShort${row.expeditionType}`, row.expeditionType);

    const createdDateTime = row.createdDate
      ? formatDateTimeFromDayjsInTzWithLocale(dayjs(row.createdDate), loggedUser.defaultTimezoneId)
      : null;
    const lastModifiedDateTime = row.lastModifiedDate
      ? formatDateTimeFromDayjsInTzWithLocale(dayjs(row.lastModifiedDate), loggedUser.defaultTimezoneId)
      : null;

    const startUtc = row.expeditionStart?.utcStart;
    const startTz = row.expeditionStart?.timezoneId ?? row.locations[0]?.timezoneId;
    const startDayjs = startUtc && startTz ? dayjsFromApiUtcInTz(startUtc, startTz) : null;
    const startDateTime = startDayjs ? formatDateTimeFromDayjsInTzWithLocale(startDayjs, startTz) : null;
    const startDateShort = startDayjs ? formatOnlyDayMonthFromDayjsInTz(startDayjs, startTz) : null;
    const startExpanded = expandedInfoTags[InfoTagEnum.START_DATE];

    const iaAdditionalInfoAvailable = expeditionType
      || startDateTime
      || (stopCount > 0)
      || (row.orders && orderCount > 0)
      || (hasPermission([PermissionTypesEnum.PERMISSION_EXPEDITION_LIST_RW, PermissionTypesEnum.PERMISSION_TIMESLOT_LIST_READ]) && row.extraCosts && row.extraCosts.length > 0)
      || isNotBlank(row.note)
      || ((row as any).attachmentInfo && (row as any).attachmentInfo.attachmentSize > 0)
      || row.lastModifiedDate
      || row.createdDate;

    return (
      <div className="flex flex-col gap-2 items-start">
        <div className="flex flex-wrap gap-2 items-start">
          {iaAdditionalInfoAvailable && (
            <ExpandableInfoAllTag
              id={`all_${enc(row.applicationId)}`}
              dataTestId={`carrier-expeditions-row-${row.applicationId}-additional-all`}
              onClick={() => handleAllTagClick(InfoTagEnum.ALL)}
              tooltipText={expandedInfoTags['all'] ? tr(`expandableInfoTag.collapseAll`, 'Collapse all') : tr(`expandableInfoTag.expandAll`, 'Expand all')}
              icon={
                <SvgChevron className={clsx('text-content-brand',
                  expandedInfoTags['all'] && 'rotate-180')}
                />
              }
            />
          )}
          {startDateTime && (
            <ExpandableInfoTag
              id={`startDate_${enc(row.applicationId)}`}
              dataTestId={`carrier-expeditions-row-${row.applicationId}-additional-startDate`}
              expanded={startExpanded}
              onClick={() => handleTagClick(InfoTagEnum.START_DATE)}
              icon={<SvgCalendar className={'text-content-secondary'} />}
              iconText={startExpanded ? undefined : startDateShort}
              label={tr('CarrierShipmentsList.startDateTagLabel', 'Start date: ')}
              text={startDateTime}
            />
          )}
          {expeditionType && (
            <ExpandableInfoTag
              id={`type_${enc(row.applicationId)}`}
              expanded={expandedInfoTags[InfoTagEnum.EXPEDITION_TYPE]}
              onClick={() => handleTagClick(InfoTagEnum.EXPEDITION_TYPE)}
              iconText={expeditionType}
            />
          )}
          {stopCount > 0 && (
            <ExpandableInfoTag
              id={`stops_${enc(row.applicationId)}`}
              dataTestId={`carrier-expeditions-row-${row.applicationId}-additional-stops`}
              expanded={expandedInfoTags[InfoTagEnum.STOPS]}
              onClick={() => handleTagClick(InfoTagEnum.STOPS)}
              label={tr('CarrierShipmentsList.stopsTagLabel', 'Stops: ')}
              text={stops}
              icon={<SvgRoute />}
              count={stopCount}
            />
          )}

          {row.orders && orderCount > 0 && (
            <ExpandableInfoTag
              id={`orders_${enc(row.applicationId)}`}
              dataTestId={`carrier-expeditions-row-${row.applicationId}-additional-orders`}
              expanded={expandedInfoTags[InfoTagEnum.ORDERS]}
              onClick={() => handleTagClick(InfoTagEnum.ORDERS)}
              label={tr('CarrierShipmentsList.ordersTagLabel', 'Orders: ')}
              text={ordersTooltipContent}
              icon={<SvgOrder />}
              count={orderCount}
            />
          )}

          {hasPermission([PermissionTypesEnum.PERMISSION_EXPEDITION_LIST_RW, PermissionTypesEnum.PERMISSION_TIMESLOT_LIST_READ]) &&
            row.extraCosts &&
            row.extraCosts.length > 0 && (
              <ExpandableInfoTag
                id={`extraCosts_${enc(row.applicationId)}`}
                dataTestId={`carrier-expeditions-row-${row.applicationId}-additional-extraCosts`}
                expanded={expandedInfoTags[InfoTagEnum.EXTRA_COSTS]}
                onClick={() => handleTagClick(InfoTagEnum.EXTRA_COSTS)}
                label={tr('CarrierShipmentsList.extraCostsTagLabel', 'Extra Costs: ')}
                text={extraCostsTooltipContent}
                icon={<SvgMoney />}
                count={row.extraCosts.length}
              />
            )}

          {isNotBlank(row.note) && (
            <ExpandableInfoTag
              id={`note_${enc(row.applicationId)}`}
              dataTestId={`carrier-expeditions-row-${row.applicationId}-additional-note`}
              expanded={expandedInfoTags[InfoTagEnum.NOTE]}
              onClick={() => handleTagClick(InfoTagEnum.NOTE)}
              label={tr('CarrierShipmentsList.noteTagLabel', 'Note: ')}
              text={noteToPlainText(row.note)}
              textComponent={<SafeHtml html={row.note} />}
              icon={<SvgFile />}
            />
          )}

          {row.attachmentInfo && row.attachmentInfo.attachmentSize > 0 && (
            <ExpandableInfoTag
              id={`attachments_${enc(row.applicationId)}`}
              dataTestId={`carrier-expeditions-row-${row.applicationId}-additional-attachments`}
              expanded={expandedInfoTags[InfoTagEnum.ATTACHMENTS]}
              onClick={() => handleTagClick(InfoTagEnum.ATTACHMENTS)}
              icon={
                <SvgLink className={expandedInfoTags[InfoTagEnum.ATTACHMENTS] ? 'text-content-brand' : ''} />
              }
              label={tr('CarrierShipmentsList.attachmentTagLabel', 'Attachments: ')}
              count={row.attachmentInfo.attachmentSize}
              text={
                row.attachmentInfo.attachments
                  .map((attachment) => truncateFileName(attachment.name))
                  .join(' | ')
              }
              textComponent={
                <span className="font-medium">
                  {row.attachmentInfo.attachments.map((attachment, idx) => (
                    <React.Fragment key={attachment.id}>
                      <AttachmentLink
                        id={attachment.id}
                        name={attachment.name}
                        onActivate={(id, fileName) => {
                          handleFileClick(id, fileName);
                        }}
                        maxLength={15}
                      />
                      {idx < row.attachmentInfo.attachments.length - 1 && (
                        <span className="text-content-tertiary">{'\u00A0|\u00A0'}</span>
                      )}
                    </React.Fragment>
                  ))}
                </span>
              }
            />
          )}

          {row.lastModifiedDate && (
            <ExpandableInfoTag
              id={`modifiedDate_${enc(row.applicationId)}`}
              dataTestId={`carrier-expeditions-row-${row.applicationId}-additional-lastModifiedDate`}
              expanded={expandedInfoTags[InfoTagEnum.LAST_MODIFIED_DATE]}
              onClick={() => handleTagClick(InfoTagEnum.LAST_MODIFIED_DATE)}
              label={tr('CarrierShipmentsList.modifiedDateTagLabel', 'Last modified: ')}
              text={lastModifiedDateTime}
              icon={<SvgRepeatBack />}
            />
          )}

          {row.createdDate && (
            <ExpandableInfoTag
              id={`createdDate_${enc(row.applicationId)}`}
              dataTestId={`carrier-expeditions-row-${row.applicationId}-additional-createdDate`}
              expanded={expandedInfoTags[InfoTagEnum.CREATED_DATE]}
              onClick={() => handleTagClick(InfoTagEnum.CREATED_DATE)}
              label={tr('CarrierShipmentsList.createdDateTagLabel', 'Created: ')}
              text={createdDateTime}
              icon={<SvgCreated />}
            />
          )}

        </div>
      </div>
    );
  }

  const renderTender = (row: JobPreviewOutDTO, isMobileCard?: boolean) => {
    const totalPrice = row.price + (row.extraCosts?.reduce((a, b) => a + b.price, 0) ?? 0);

    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '0.25rem' }}>
        {!isMobileCard && <span className="p-column-title">{tr('CarrierShipmentsList.tenderTitle', 'Tendr')}</span>}
        <>
          <span
            data-testid={`carrier-expeditions-row-${row.applicationId}-assigned-price`}
            data-assigned-total-price={String(totalPrice)}
            data-assigned-currency={row.currency ?? ''}
            style={{ display: 'none' }}
          />
          {hasPermission([PermissionTypesEnum.PERMISSION_EXPEDITION_PRICE, PermissionTypesEnum.PERMISSION_EXPEDITION_PRICE]) && (
            <PriceTag>{formatPrice(totalPrice, row.currency)}</PriceTag>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '0.25rem' }}>
            {row.customerCompanyName && (
              <div
                className={"text-[#616061] font-sans text-xs font-normal leading-normal tracking-[0.24px] flex items-center gap-2"}
                data-testid={`carrier-expeditions-row-${row.applicationId}-customerName`}
              >
                <SvgBuilding />
                <span>{row.customerCompanyName}</span>
              </div>
            )}
            {(row.assignedDriver || row.carPlate) && (
              <div className={"text-[#616061] font-sans text-xs font-normal leading-normal tracking-[0.24px] flex items-center gap-2"}>
                <SvgTruck />
                <span data-testid={`carrier-expeditions-row-${row.applicationId}-driverInfo`}>{row.assignedDriver}{row.assignedDriver && row.carPlate ? ', ' : ''}{row.carPlate}</span>
              </div>
            )}
          </div>
        </>
      </div>
    );
  };

  const renderIdColumn = (rowData: JobPreviewOutDTO) => {
    return (
      <div className={'flex flex-col items-start gap-2'}>
        <Link
          to={navigation.createNavigationLink(navigation.urlFunctions.createCarrierShipmentDetail(rowData.companyId, rowData.applicationId))}>
          <span className={'text-[16px] font-normal leading-normal tracking-[0.24px] text-content-brand font-dm-sans'}>
            {rowData.applicationId}
          </span>
        </Link>
        <SmallerAndGrayDiv data-testid={`carrier-expeditions-row-${rowData.applicationId}-dispatcher`}>{rowData.dispatcherFullName}</SmallerAndGrayDiv>
        <ExpandableLabelWrapper
          labels={rowData.labels}
          isExpanded={expandedLabels}
          onToggle={toggleLabelsExpansion}
          />
      </div>
    );
  };

  const tableRef = useRef<{ reloadData: () => void, selectedObjects: JobPreviewOutDTO[], clear: () => void }>(null);

  const {
    MultiEditCarInfoSidebar,
    setVisible: setVisibleMultiEditCarIfoSidebar,
  } = useCreateMultiEditCarInfoSidebar(() => {
    tableRef.current?.clear();
    tableRef.current?.reloadData();
    setVisibleMultiEditCarIfoSidebar(false);
  });

  // Add mobile card renderer
  const renderMobileCard = (row: JobPreviewOutDTO, checkbox?: JSX.Element | null) => {
    const missingTimeslots = !row.jobAlerts.hasSelectedTimeslots;
    const missingCarPlate = !row.carPlate;
    const missingReservation = !row.jobAlerts.hasRequiredReservations;

    return (
      <MobileCard>
        <span
          data-testid={`carrier-expeditions-row-${row.applicationId}-state`}
          data-expedition-status={row.expeditionStatus ?? ''}
          style={{ display: 'none' }}
        />
        <span
          data-testid={`carrier-expeditions-row-${row.applicationId}-assigned-price`}
          data-assigned-total-price={String(row.price + (row.extraCosts?.reduce((a, b) => a + b.price, 0) ?? 0))}
          data-assigned-currency={row.currency ?? ''}
          style={{ display: 'none' }}
        />
        <MobileCardHeader>
          <div className="flex items-start gap-3">
            {checkbox}
            <Link
              to={navigation.createNavigationLink(navigation.urlFunctions.createCarrierShipmentDetail(row.companyId, row.applicationId))}>
              <span className={'text-[16px] font-normal leading-normal tracking-[0.24px] text-content-brand font-dm-sans'}>
                {row.applicationId}
              </span>
            </Link>
          </div>
          <ExpeditionStateSpan expeditionStage={row.expeditionStatus} isManual={row.isManualExpedition} />
        </MobileCardHeader>

        <MobileCardContent>
          {/* Dispatcher */}
          {row.dispatcherFullName && (
            <MobileSection>
              <MobileSectionTitle>{tr('CarrierShipmentsList.mobileCardDispatcher', 'Dispatcher')}</MobileSectionTitle>
              <SmallerAndGrayDiv>{row.dispatcherFullName}</SmallerAndGrayDiv>
            </MobileSection>
          )}

          {/* Route Information */}
          <MobileSection>
            <MobileSectionTitle>{tr('CarrierShipmentsList.mobileCardRoute', 'Route')}</MobileSectionTitle>
            <div className="min-w-0 w-full">
              <div data-testid={`carrier-expeditions-row-${row.applicationId}-route-from`} className="flex items-center gap-1 min-w-0">
                <CountryFlag code={row.locations[0].country} />
                <b className="flex-shrink-0">{row.locations[0].country}</b>
                <span id={`mobile-route-from-name_${enc(row.applicationId)}`} className="truncate">{row.locations[0].locationName}</span>
                <CustomTooltip targetSelector={`#mobile-route-from-name_${enc(row.applicationId)}`} content={row.locations[0].locationName} />
              </div>
              <SmallerAndGrayDiv>{formatTimeslotInterval(row.locations[0].globalTimeslots)}</SmallerAndGrayDiv>
              <ConditionalLinkedResBadge route={row.routes[0]} />
              {row.locations.length > 1 && (
                <>
                  <div style={{ marginTop: '0.5rem' }} data-testid={`carrier-expeditions-row-${row.applicationId}-route-to`} className="flex items-center gap-1 min-w-0">
                    <CountryFlag code={row.locations[row.locations.length - 1].country} />
                    <b className="flex-shrink-0">{row.locations[row.locations.length - 1].country}</b>
                    <span id={`mobile-route-to-name_${enc(row.applicationId)}`} className="truncate">{row.locations[row.locations.length - 1].locationName}</span>
                    <CustomTooltip targetSelector={`#mobile-route-to-name_${enc(row.applicationId)}`} content={row.locations[row.locations.length - 1].locationName} />
                  </div>
                  <SmallerAndGrayDiv>{formatTimeslotInterval(row.locations[row.locations.length - 1].globalTimeslots)}</SmallerAndGrayDiv>
                  <ConditionalLinkedResBadge route={row.routes[row.routes.length - 1]} />
                </>
              )}
            </div>
          </MobileSection>

          {/* Tender Information */}
          <MobileSection>
            <MobileSectionTitle>{tr('CarrierShipmentsList.mobileCardTender', 'Tender')}</MobileSectionTitle>
            {renderTender(row, true)}
          </MobileSection>

          {/* Alerts */}
          {(missingTimeslots || missingCarPlate || missingReservation) && (
            <MobileSection>
              <MobileSectionTitle>{tr('CarrierShipmentsList.mobileCardAlerts', 'Alerts')}</MobileSectionTitle>
              <div className="flex flex-col gap-1">
                {missingTimeslots && (
                  <TenderAlert message={tr('CarrierShipmentsList.missingTimeslotsAlert', 'Missing timeslots')} />
                )}
                {missingCarPlate && (
                  <TenderAlert message={tr('CarrierShipmentsList.carPlateAlert', 'No car plate assigned')} />
                )}
                {missingReservation && (
                  <TenderAlert message={tr('CarrierShipmentsList.reservationAlert', 'Missing reservation')} />
                )}
              </div>
            </MobileSection>
          )}

          {/* Additional Info - Collapsible on mobile */}
          <MobileSection>
            <MobileSectionTitle
              onClick={() => toggleInfoExpansion(InfoTagEnum.ALL)}
              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              {tr('CarrierShipmentsList.mobileCardAdditionalInfo', 'Additional Info')}
              <span style={{ transform: expandedInfoTags[InfoTagEnum.ALL] ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                ▶
              </span>
            </MobileSectionTitle>
            {expandedInfoTags[InfoTagEnum.ALL] && (
              <div className="flex flex-col gap-2">
                <div className="flex flex-wrap gap-2">
                  {renderMobileAdditionalInfo(row)}
                </div>
              </div>
            )}
          </MobileSection>

          {/* Labels */}
          {row.labels && row.labels.length > 0 && (
            <MobileSection>
              <MobileSectionTitle>{tr('CarrierShipmentsList.mobileCardLabels', 'Labels')}</MobileSectionTitle>
              <div className="flex flex-wrap gap-1">
                {row.labels
                  .sort((a, b) => a.labelValue.localeCompare(b.labelValue))
                  .map((label, index) => (
                    <MobileLabelDiv color={label.labelColor} key={index}>
                      {label.labelValue}
                    </MobileLabelDiv>
                  ))}
              </div>
            </MobileSection>
          )}

        </MobileCardContent>
      </MobileCard>
    );
  };

  // Mobile-friendly additional info renderer (no tooltips, always expanded)
  const renderMobileAdditionalInfo = (row: JobPreviewOutDTO) => {
    const stopCount = row.locations.length - 2;
    const missingStopsTimeslots = !row.jobAlerts.hasSelectedTimeslotsOnStops;
    const expeditionType = !row.expeditionType ? null : tr(`global.expeditionTypeShort${row.expeditionType}`, row.expeditionType);
    const extraCostsTooltipContent = row.extraCosts?.map(ecItem => `${ecItem.name}: ${formatPrice(ecItem.price, ecItem.currency)}`).join('; ') || '';
    const createdDate = row.createdDate ? formatOnlyDayMonthFromDayJs(dayjs(row.createdDate)) : null;
    const lastModifiedDate = row.lastModifiedDate ? formatOnlyDayMonthFromDayJs(dayjs(row.lastModifiedDate)) : null;

    const tags = [];

    if (expeditionType) {
      tags.push(
        <MobileInfoTag key="type">
          {/*<span className="tag-label">Type: </span>*/}
          <span className="tag-text">{expeditionType}</span>
        </MobileInfoTag>
      );
    }

    if (stopCount > 0) {
      const stops = row.locations
        ?.slice(1, -1)
        .map(loc => loc.locationName)
        .join(', ') || '';
      tags.push(
        <MobileInfoTag key="stops" backgroundColor={missingStopsTimeslots ? 'var(--ringil3-content-brandMedium)' : undefined}>
          <SvgRoute />
          <span className="tag-label">{tr('CarrierShipmentsList.stopsTagLabel', 'Stops: ')}</span>
          <span className="tag-text">{stops}</span>
        </MobileInfoTag>
      );
    }

    if (hasPermission([PermissionTypesEnum.PERMISSION_EXPEDITION_LIST_RW, PermissionTypesEnum.PERMISSION_TIMESLOT_LIST_READ]) &&
        row.extraCosts && row.extraCosts.length > 0) {
      tags.push(
        <MobileInfoTag key="extraCosts">
          <SvgMoney />
          <span className="tag-label">{tr('CarrierShipmentsList.extraCostsTagLabel', 'Extra Costs: ')}</span>
          <span className="tag-text">{extraCostsTooltipContent}</span>
        </MobileInfoTag>
      );
    }

    if (isNotBlank(row.note)) {
      tags.push(
        <MobileInfoTag key="note">
          <SvgFile />
          <span className="tag-label">{tr('CarrierShipmentsList.noteTagLabel', 'Note: ')}</span>
          <span className="tag-text"><SafeHtml html={row.note} /></span>
        </MobileInfoTag>
      );
    }

    if ((row as any).attachmentInfo && (row as any).attachmentInfo.attachmentSize > 0) {
      (row as any).attachmentInfo.attachments.forEach((attachment: { id: string; name: string }) => {
        tags.push(
          <MobileFileTag
            key={attachment.id}
            onClick={e => {
              e.stopPropagation();
              handleFileClick(attachment.id, attachment.name);
            }}
            title={attachment.name}
          >
            <LinkSvg />
            <span>{truncateFileName(attachment.name, 20)}</span>
          </MobileFileTag>
        );
      });
    }

    if (row.lastModifiedDate) {
      tags.push(
        <MobileInfoTag key="modified">
          <SvgRepeatBack />
          <span className="tag-label">{tr('CarrierShipmentsList.modifiedDateTagLabel', 'Last modified: ')}</span>
          <span className="tag-text">{lastModifiedDate}</span>
        </MobileInfoTag>
      );
    }

    if (row.createdDate) {
      tags.push(
        <MobileInfoTag key="created">
          <SvgCreated />
          <span className="tag-label">{tr('CarrierShipmentsList.createdDateTagLabel', 'Created: ')}</span>
          <span className="tag-text">{createdDate}</span>
        </MobileInfoTag>
      );
    }

    return tags;
  };

  // Update column config to be responsive - maintain consistent structure
  const columnsConfig: ColumnConfig<JobPreviewOutDTO>[] = [
    {
      id: 'applicationId',
      header: 'CarrierShipmentsList.columnHeaderApplicationId',
      headerDefault: 'Expedice',
      width: isMobile ? '100%' : '10%',
      sortable: true,
      field: 'applicationId',
      order: 1,
      render: renderIdColumn,
    },
    {
      id: 'expeditionStatus',
      header: 'CarrierShipmentsList.columnHeaderExpeditionStatus',
      headerDefault: 'Stav',
      width: '15%',
      sortable: true,
      field: 'expeditionStatus',
      order: 2,
      render: renderExpeditionStatus,
      hidden: isMobile, // Hide on mobile instead of removing from array
    },
    {
      id: 'routes',
      header: 'CarrierShipmentsList.columnHeaderRoute',
      headerDefault: 'Trasa',
      width: '25%',
      sortable: true,
      field: 'compositeStartUtc',
      order: 3,
      render: renderRoute,
      hidden: isMobile, // Hide on mobile instead of removing from array
    },
    {
      id: 'tender',
      header: 'CarrierShipmentsList.columnHeaderTender',
      headerDefault: 'Tendr',
      width: '20%',
      sortable: false,
      field: 'tender',
      order: 4,
      render: renderTender,
      hidden: isMobile, // Hide on mobile instead of removing from array
    },
    {
      id: 'additionalInfo',
      header: 'CarrierShipmentsList.columnHeaderAdditionalInfo',
      headerDefault: 'Další informace',
      width: '30%',
      sortable: true,
      isDefaultSort: true,
      field: 'createdDateInUtc',
      order: 5,
      render: renderAdditionalInfo,
      hidden: isMobile, // Hide on mobile instead of removing from array
    },
  ] as ColumnConfig<JobPreviewOutDTO>[];

  const filtersConf: FilterConfig[] = [
    {
      filterType: FilterType.InputText,
      placeholder: tr(`Shipments.applicationId`, `Application ID`),
      value: ['applicationId'],
      dataTestId: 'carrier-expeditions-filter-applicationId',
    },
    {
      filterType: FilterType.Dropdown,
      placeholder: tr(`Shipments.expeditionStage`, `Expedition stage`),
      value: ['carrierExpeditionStatus'],
      options: Object.keys(CarrierExpeditionStatusEnum).map(i => ({
          label: t(`Shipments.${i}`, i),
          value: i,
          active: true,
        })),
    },
    {
      filterType: FilterType.Calendar,
      placeholder: tr(`Shipments.loading`, `Loading`),
      value: ['loadingDateFromISO', 'loadingDateToISO'],
      dateTimeFormat: DateTimeFormatType.ISOString,
    },
    {
      filterType: FilterType.Calendar,
      placeholder: tr(`Shipments.unloading`, `Unloading`),
      value: ['unloadingDateFromISO', 'unloadingDateToISO'],
      dateTimeFormat: DateTimeFormatType.ISOString,
    },
    {
      filterType: FilterType.MultiSelect,
      placeholder: tr('Shipments.dispatchers', 'Dispatchers'),
      value: ['carrierDispatcherIds'],
      options: dispatchers.map(d => ({ label: d.name, value: d.id, active: true })),
    },
    {
      filterType: FilterType.Dropdown,
      placeholder: tr(`Shipments.loadingCountry`, `Loading country`),
      value: ['loadingCountryCode'],
      options: countries.map(c => ({ label: c.name, value: c.code, active: true })),
    },
    {
      filterType: FilterType.Dropdown,
      placeholder: tr(`Shipments.unloadingCountry`, `Unloading country`),
      value: ['unloadingCountryCode'],
      options: countries.map(c => ({ label: c.name, value: c.code, active: true })),
    },
    {
      filterType: FilterType.InputText,
      placeholder: tr(`Shipments.locationName`, `Location name`),
      value: ['locationName'],
    },
    {
      filterType: FilterType.InputText,
      placeholder: tr(`Shipments.orderName`, `Order name`),
      value: ['orderName'],
      dataTestId: 'carrier-expeditions-filter-orderName',
    },
    {
      filterType: FilterType.MultiSelectLazy,
      placeholder: tr(`Shipments.driver`, `Driver`),
      value: ['driverIds'],
      options: drivers.map(d => ({ label: d.name, value: d.id, active: true })),
      lazyLoading: lazyLoading,
      onLazyLoad: onLazyLoad,
    },
    {
      filterType: FilterType.Dropdown,
      placeholder: tr(`Shipments.customer`, `Customer`),
      value: ['customerCompanyId'],
      options: customers.map(c => ({label: c.name, value: c.id, active: true})),
    },
    ...(attachmentNameFilterFlag.granted
      ? [
          {
            filterType: FilterType.InputText,
            placeholder: tr('generic.attachmentName', 'Attachment name'),
            value: ['attachmentName'],
            dataTestId: 'carrier-expeditions-filter-attachmentName',
          } as FilterConfig,
        ]
      : []),
  ]

const massActionConfig: MassActionConfig[] = [
  {
    label: tr('CarrierShipmentsList.nahratDokumenty', 'Nahrat dokumenty'),
    onClick: () => setVisibleFileUploadDialog(true),
  },
  {
    label: tr('CarrierShipmentsList.changeCarInfo', 'Mass update car info'),
    onClick: () => setVisibleMultiEditCarIfoSidebar(true),
  },
];

  return (
    <>
      <div className={'flex flex-col gap-2'}>
        <BreadCrumbComponent items={[{ label: tr(`CarrierShipmentsList.shipments`, `Shipments`) }]} />
        <COHeadline title={tr(`CarrierShipmentsList.shipments`, `Shipments`)} variant={'h1'} />
      </div>

      <UploadAttachmentDialog
        entityType={EntityTypeEnum.EXPEDITION}
        entityIds={tableRef.current?.selectedObjects?.map(t => t.id) || []}
        visible={visibleFileUploadDialog}
        onHide={() => setVisibleFileUploadDialog(false)}
        onAttached={count => {
          setVisibleFileUploadDialog(false);
          toast.current.show({
            severity: 'success',
            summary: tr(`CarrierShipmentsList.dokumentyBylyNahrany`, `Dokumenty byly nahrany`),
            detail: tr(`CarrierShipmentsList.nahraneDokumenty`, `K expedidici byly nahrany {{count}} dokumenty`, { count }, count),
            life: 3000,
          });
          tableRef.current?.clear();
          tableRef.current?.reloadData();
        }}
      />

      <MultiEditCarInfoSidebar
        carrierExpeditionIds={tableRef.current?.selectedObjects?.map(t => {
          return {
            applicationId: t.applicationId,
            customerId: t.companyId,
          } as CarrierExpeditionId;
        }) || []}
        expeditionId={tableRef.current?.selectedObjects?.map(t => t.id)[0]}
      />

      <RingilDataTable
        ref={tableRef}
        columnConfig={columnsConfig}
        filtersConf={filtersConf}
        loadDataFromApi={loadDataApi}
        filterName={'shipments'}
        massActionConfig={massActionConfig}
        renderMobileCard={isMobile ? renderMobileCard : undefined}
        floatingMassActions={true}
      />

      <Toast ref={toast} />
    </>
  );
};

const SmallerAndGrayDiv = styled.div`
  color: #999;
  font-size: smaller;
`;

const PriceTag = styled.div`
  display: inline-block;
  background-color: #99e8bf;
  color: #343333;
  padding: 0.25rem 0.75rem;
  border-radius: 0.25rem;
  font-weight: 500;
  margin-bottom: 0.25rem;
`;

export default ResponsiveShipmentList;

